import React from 'react';
import { Box, Card, CardContent, Stack, Typography, Button, Input, CircularProgress } from '@mui/joy';
import Grid from '@mui/material/Grid2';
import { motion, AnimatePresence } from "framer-motion";

import { usePolicyWizard } from '../hooks/usePolicyWizard';
import { formatTitle, getChangedFields } from '../helpers/policyUtils';
import {
  RenderField,
  WizardStepper,
  RenderListItems,
  ImageReferencePanel,
  SettingsStep,
  NavigationBlocker,
  TestResultModal,
  EvaluationDocumentsStep
} from '../components/policywizard';
import FlowBuilder from '../components/policywizard/FlowBuilder';

const stepVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, x: -50, transition: { duration: 0.3 } }
};

const PolicyWizardPage = () => {
  const wizard = usePolicyWizard();

  const {
    policyData,
    currentPolicy,
    setCurrentPolicy,
    currentStructure,
    routePolicyId,
    currentUserId,
    stepIndex,
    setStepIndex,
    selectedIndex,
    setSelectedIndex,
    handleNext,
    handleBack,
    isLoading,
    isLoadingUsers,
    isLoadingFunctions,
    isTestLoading,
    isGeneratePolicyLoading,
    isTextCropLoading,
    users,
    functions,
    models,
    isPolicyChanged,
    isIntermediateSaveButtonDisabled,
    showImagePanel,
    handleSave,
    handleSaveOnLastStep,
    testPage,
    setTestingPage,
    clearTestingPage,
    testResultOpen,
    setTestResultOpen,
    runTestField,
    isImageReferenceDrawerOpen,
    openImageReferenceDrawer,
    closeImageReferenceDrawer,
    isGeneratedPolicyModal,
    policyDefinition,
    generatePolicy,
    applyPolicyDefinition,
    closeGeneratedPolicyModal,
    isTextCropModal,
    cropBox,
    textCropping,
    closeTextCroppingModal,
    openFullscreenModal,
    handleModalClose,
    handleFileChange,
    isEditingName,
    setIsEditingName,
    linkedCompliancePolicy,
    isUploadingDocument,
    addEvaluationDocument,
    compliancePolicies
  } = wizard;

  // Check if current policy is Evaluation type (case-insensitive since API returns lowercase)
  const isEvaluationType = currentPolicy?.settings?.type?.toLowerCase() === 'evaluation';
  const isComplianceDocFieldsStep =
    currentPolicy?.settings?.type?.toLowerCase() === 'compliance' &&
    currentStructure?.fields?.[stepIndex]?.key === 'documentFieldsSchema';

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.vars.palette.background.backdrop,
          backdropFilter: 'blur(6px)',
        })}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }} level="body-md">Loading...</Typography>
      </Box>
    );
  }

  // Get test result for modal
  const getTestResult = () => {
    if (!testResultOpen || stepIndex !== 1) return null;
    return currentPolicy.policy[currentStructure.fields[stepIndex].key][testResultOpen];
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
        minHeight: 0,
      }}
    >
      <NavigationBlocker
        whenToBlock={isPolicyChanged}
        changesToList={isPolicyChanged ? getChangedFields(policyData, currentPolicy) : null}
      />

      <Box sx={{
        flexGrow: 1,
        overflow: "hidden", // Prevent outer scroll
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Test Result Modal */}
        <TestResultModal
          open={!!testResultOpen && stepIndex === 1}
          onClose={() => setTestResultOpen(false)}
          result={getTestResult()}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={stepIndex}
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {Object.keys(currentPolicy).length > 0 && (
              <>
                {/* Step Header */}
                <Stack sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ justifyContent: "center" }}>
                    {/* Policy Name */}
                    {isEditingName ? (
                      <Input
                        value={currentPolicy.name || ''}
                        onChange={e => {
                          setCurrentPolicy({
                            ...currentPolicy,
                            name: e.target.value
                          });
                        }}
                        onBlur={() => setIsEditingName(false)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setIsEditingName(false);
                          }
                        }}
                        placeholder="Enter document flow name"
                        autoFocus
                        sx={{
                          minWidth: '150px',
                          maxWidth: '300px',
                          '& .MuiInput-input': {
                            fontSize: '2rem',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    ) : (
                      <Typography
                        level="h1"
                        textTransform='capitalize'
                        onClick={() => setIsEditingName(true)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {currentPolicy.name || 'Click to enter policy name'}
                      </Typography>
                    )}
                    <Typography level="h1" sx={{ color: 'neutral.400' }}>/</Typography>
                    <Typography level="h1" textTransform="capitalize">
                      {stepIndex < currentStructure?.fields?.length
                        ? formatTitle(currentStructure.fields[stepIndex].key)
                        : "Settings"}
                    </Typography>
                  </Stack>
                  <Typography
                    level="body-sm"
                    sx={{ textAlign: "center", whiteSpace: "pre-line" }}
                  >
                    {stepIndex < currentStructure?.fields?.length
                      ? currentStructure.fields?.[stepIndex]?.info?.replace(/\. /g, ".\n") || "Set the settings for the policy"
                      : "Set the settings for the policy"}
                  </Typography>
                </Stack>

                {/* Main Content Card */}
                <Card sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0
                }}>
                  <CardContent sx={{
                    flex: 1,
                    overflowY: isComplianceDocFieldsStep ? 'hidden' : 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        display: "flex",
                        flex: 1,
                        minHeight: 0,
                        height: isComplianceDocFieldsStep ? "100%" : "auto",
                        alignItems: "stretch"
                      }}
                    >
                      <Grid
                        size={{
                          xs: 12,
                          md: 12,
                          lg: 12
                        }}
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          height: isComplianceDocFieldsStep ? "100%" : "auto",
                        }}
                      >
                        {/* Step Content */}
                        {stepIndex < currentStructure.fields.length && (
                          <>
                            {/* Evaluation Documents Step */}
                            {isEvaluationType && currentStructure.fields[stepIndex].type === "evaluation-documents" ? (
                              <EvaluationDocumentsStep
                                currentPolicy={currentPolicy}
                                setCurrentPolicy={setCurrentPolicy}
                                linkedCompliancePolicy={linkedCompliancePolicy}
                                addEvaluationDocument={addEvaluationDocument}
                                isUploadingDocument={isUploadingDocument}
                              />
                            ) : currentStructure.fields[stepIndex].type === "flow-builder" ? (
                              <FlowBuilder
                                value={currentPolicy.policy[currentStructure.fields[stepIndex].key]}
                                onChange={newValue => {
                                  setCurrentPolicy({
                                    ...currentPolicy,
                                    policy: {
                                      ...currentPolicy.policy,
                                      [currentStructure.fields[stepIndex].key]: newValue
                                    }
                                  });
                                }}
                              />
                            ) : (
                              <>
                                <RenderField
                                  type={currentStructure.fields[stepIndex].type}
                                  value={currentPolicy.policy[currentStructure.fields[stepIndex].key]}
                                  onChange={newValue => {
                                    setCurrentPolicy({
                                      ...currentPolicy,
                                      policy: {
                                        ...currentPolicy.policy,
                                        [currentStructure.fields[stepIndex].key]: newValue
                                      }
                                    });
                                  }}
                                />
                                {currentStructure.fields[stepIndex].type === "list" && (
                                  <RenderListItems
                                    currentPolicy={currentPolicy}
                                    currentStructure={currentStructure}
                                    stepIndex={stepIndex}
                                    setCurrentPolicy={setCurrentPolicy}
                                    onOpenImageReferenceDrawer={openImageReferenceDrawer}
                                    isGeneratePolicyLoading={isGeneratePolicyLoading}
                                    generatePolicy={generatePolicy}
                                    isTextCropLoading={isTextCropLoading}
                                    textCropping={textCropping}
                                    disableTest={!testPage}
                                    setTestResultOpen={setTestResultOpen}
                                    selectedIndex={selectedIndex}
                                    setSelectedIndex={setSelectedIndex}
                                    testPage={testPage}
                                    models={models}
                                  />
                                )}
                              </>
                            )}
                          </>
                        )}


                        {/* Settings Step */}
                        {stepIndex === currentStructure.fields.length && (
                          <SettingsStep
                            currentPolicy={currentPolicy}
                            setCurrentPolicy={setCurrentPolicy}
                            currentStructure={currentStructure}
                            models={models}
                            functions={functions}
                            isLoadingFunctions={isLoadingFunctions}
                            users={users}
                            isLoadingUsers={isLoadingUsers}
                            currentUserId={currentUserId}
                            compliancePolicies={compliancePolicies}
                            isEvaluationType={isEvaluationType}
                          />
                        )}
                      </Grid>

                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {showImagePanel && (
          <ImageReferencePanel
            open={isImageReferenceDrawerOpen}
            onClose={closeImageReferenceDrawer}
            testPage={testPage}
            setTestingPage={setTestingPage}
            clearTestingPage={clearTestingPage}
            selectedIndex={selectedIndex}
            runTestField={runTestField}
            isTestLoading={isTestLoading}
            isGeneratedPolicyModal={isGeneratedPolicyModal}
            closeGeneratedPolicyModal={closeGeneratedPolicyModal}
            policyDefinition={policyDefinition}
            applyPolicyDefinition={applyPolicyDefinition}
            isTextCropModal={isTextCropModal}
            closeTextCroppingModal={closeTextCroppingModal}
            cropBox={cropBox}
            openModal={openFullscreenModal}
            handleModalClose={handleModalClose}
            onFileChange={handleFileChange}
          />
        )}
      </Box>

      {/* Footer Navigation */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        zIndex: 1000,
        flexShrink: 0,
        py: 1, // Add some padding for better look
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface'
      }}>
        <Stack direction='row'
          sx={{
            alignItems: 'center',
            flexGrow: 1,
            p: 1,
            justifyContent: 'space-around',
          }}
        >
          <Button onClick={handleBack} disabled={stepIndex === 0}>
            Back
          </Button>

          <WizardStepper
            currentStructure={currentStructure}
            stepIndex={stepIndex}
            setStepIndex={setStepIndex}
            setSelectedIndex={setSelectedIndex}
          />

          <Stack direction="row" spacing={2}>
            <Button
              onClick={stepIndex === (currentStructure.fields?.length || 0) ? handleSaveOnLastStep : handleNext}
              color={stepIndex === (currentStructure.fields?.length || 0) ? "success" : "primary"}
              disabled={stepIndex === (currentStructure.fields?.length || 0) && !isPolicyChanged && !!routePolicyId}
            >
              {stepIndex === (currentStructure.fields?.length || 0) ? "Save" : "Next"}
            </Button>

            {stepIndex !== (currentStructure.fields?.length || 0) && (
              <Button
                disabled={isIntermediateSaveButtonDisabled}
                onClick={handleSave}
                color="success"
              >
                Save
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default PolicyWizardPage;
